import { Controller } from "@hotwired/stimulus"
import { apiGet, formatMoney, t } from "../api_helper"
import { isOfflineError, showOfflinePlaceholder } from "../lib/offline_ui"
import { icon } from "../lib/icons"

export default class extends Controller {
  static targets = ["summary", "vsBudget", "categories", "weekly", "aiComment", "empty"]

  connect() {
    this.load()
  }

  async load() {
    try {
      const data = await apiGet("/api/forecast")
      this.render(data)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.summaryTarget, () => this.load()); return }
      this.showEmpty()
    }
  }

  render(data) {
    this.renderSummary(data)
    this.renderVsBudget(data)
    this.renderCategories(data)
    this.renderWeekly(data)
    this.renderAiComment(data)
  }

  renderSummary(data) {
    const confidenceLabels = { high: t("forecast.confidence_high"), medium: t("forecast.confidence_medium"), low: t("forecast.confidence_low") }
    const confidenceColors = { high: "#22C55E", medium: "#F59E0B", low: "#EF4444" }

    this.summaryTarget.innerHTML = `
      <div class="text-center p-5">
        <div class="text-xs font-medium mb-1 text-gray-400 dark:text-gray-500">
          ${t("forecast.subtitle")} · ${data.period_key}
        </div>
        <div class="text-3xl font-bold text-white mb-3" style="letter-spacing: -0.5px;">
          ${formatMoney(data.total_forecast, data.currency)}
        </div>
        <div class="flex justify-center gap-6 mb-3">
          <div>
            <div class="text-xs text-white/70">${t("forecast.income")}</div>
            <div class="text-sm font-semibold text-emerald-300">${formatMoney(data.total_income_forecast, data.currency)}</div>
          </div>
          <div class="w-px bg-white/20"></div>
          <div>
            <div class="text-xs text-white/70">${t("forecast.savings")}</div>
            <div class="text-sm font-semibold ${data.predicted_savings >= 0 ? 'text-emerald-300' : 'text-red-300'}">
              ${formatMoney(data.predicted_savings, data.currency)}
            </div>
          </div>
        </div>
        <div class="flex items-center justify-center gap-2">
          <span class="inline-block w-2 h-2 rounded-full" style="background-color: ${confidenceColors[data.confidence] || '#6B7280'};"></span>
          <span class="text-xs text-white/60">
            ${confidenceLabels[data.confidence] || data.confidence} · ${t("forecast.data_months").replace("%{months}", data.data_months)}
          </span>
        </div>
      </div>
    `
  }

  renderVsBudget(data) {
    if (!data.vs_budget || data.vs_budget.total_budget <= 0) return
    const vb = data.vs_budget
    const pct = Math.min((data.total_forecast / vb.total_budget * 100), 150).toFixed(0)
    const exceeds = vb.forecast_exceeds
    const color = exceeds ? "rgb(239,68,68)" : "rgb(34,197,94)"

    this.vsBudgetTarget.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
        <div class="flex justify-between items-center mb-2">
          <span class="text-xs font-medium text-gray-700 dark:text-gray-300">${t("forecast.vs_budget")}</span>
          <span class="text-xs font-semibold" style="color: ${color};">${pct}%</span>
        </div>
        <div class="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div class="h-full rounded-full transition-all duration-500" style="width: ${Math.min(pct, 100)}%; background-color: ${color};"></div>
        </div>
        <div class="text-xs mt-1.5 text-gray-400">
          ${exceeds
            ? t("forecast.exceeds_budget").replace("%{amount}", formatMoney(vb.overage, data.currency))
            : t("forecast.within_budget")}
        </div>
      </div>
    `
    this.vsBudgetTarget.classList.remove("hidden")
  }

  renderCategories(data) {
    if (!data.category_forecasts || data.category_forecasts.length === 0) return
    const max = data.category_forecasts[0]?.forecast || 1

    const items = data.category_forecasts.map(c => `
      <div class="flex items-center gap-3 py-2.5">
        <div class="flex items-center justify-center w-9 h-9 rounded-lg text-lg" style="background-color: ${c.color}20;">${c.icon}</div>
        <div class="flex-1 min-w-0">
          <div class="flex justify-between items-center mb-1">
            <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">${c.name}</span>
            <span class="text-sm font-semibold text-red-500">${formatMoney(c.forecast, data.currency)}</span>
          </div>
          <div class="h-1 rounded-full bg-gray-100 dark:bg-gray-700">
            <div class="h-full rounded-full transition-all duration-300" style="width: ${(c.forecast / max * 100).toFixed(0)}%; background-color: ${c.color};"></div>
          </div>
        </div>
      </div>
    `).join("")

    this.categoriesTarget.innerHTML = `
      <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">${t("forecast.categories")}</h2>
      <div class="bg-white dark:bg-gray-800 rounded-xl px-4 shadow-sm">${items}</div>
    `
  }

  renderWeekly(data) {
    if (!data.weekly_forecasts || data.weekly_forecasts.length === 0) return
    const max = Math.max(...data.weekly_forecasts.map(w => w.amount))

    const bars = data.weekly_forecasts.map(w => `
      <div class="flex-1 text-center">
        <div class="h-20 flex items-end justify-center mb-1">
          <div class="w-full max-w-[40px] rounded-t-lg bg-gradient-to-t from-indigo-500 to-purple-500 transition-all duration-300" style="height: ${(w.amount / max * 100).toFixed(0)}%;"></div>
        </div>
        <div class="text-xs font-medium text-gray-700 dark:text-gray-300">${t("forecast.week").replace("%{number}", w.week)}</div>
        <div class="text-xs text-gray-400">${formatMoney(w.amount, data.currency)}</div>
      </div>
    `).join("")

    this.weeklyTarget.innerHTML = `
      <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">${t("forecast.weekly")}</h2>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
        <div class="flex gap-2">${bars}</div>
      </div>
    `
    this.weeklyTarget.classList.remove("hidden")
  }

  renderAiComment(data) {
    if (!data.ai_comment) return
    this.aiCommentTarget.innerHTML = `
      <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5">${icon("sparkles", "w-4 h-4 text-indigo-400")} ${t("forecast.ai_comment")}</h2>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
        <div class="text-sm text-gray-600 dark:text-gray-400" style="line-height: 1.5;">${data.ai_comment}</div>
      </div>
    `
    this.aiCommentTarget.classList.remove("hidden")
  }

  showEmpty() {
    this.summaryTarget.innerHTML = ""
    this.summaryTarget.className = ""
    this.emptyTarget.innerHTML = `
      <div class="text-center py-12">
        <div class="flex justify-center mb-3 text-gray-300">${icon("chart-bar", "w-10 h-10")}</div>
        <div class="text-sm text-gray-400">${t("forecast.no_data")}</div>
      </div>
    `
    this.emptyTarget.classList.remove("hidden")
  }
}
